// Avoid importing tiktoken in browser bundles due to WASM/CSP constraints
// We'll fall back to heuristic counting in the browser.
import { logger } from './loggingService';

type TiktokenModel = string;

export class TokenCountingService {
  private static encodingCache = new Map<string, any>();
  private static tiktokenAvailable: boolean | null = null;

  private static isBrowser(): boolean {
    return typeof window !== 'undefined';
  }

  private static ensureTiktoken(): any | null {
    // Never attempt to load in browser
    if (this.isBrowser()) return null;
    try {
      if (this.tiktokenAvailable === null) {
         
        const tk = require('tiktoken');
        this.tiktokenAvailable = !!tk?.encoding_for_model;
        if (!this.tiktokenAvailable) return null;
        return tk;
      }
       
      const tk = require('tiktoken');
      return tk;
    } catch {
      this.tiktokenAvailable = false;
      return null;
    }
  }

  static countTokensOpenAI(text: string, model: string): number {
    try {
      const tiktokenModel = this.mapOpenAIModel(model);
      const tk = this.ensureTiktoken();
      if (!tk) {
        // Heuristic in browser or when tiktoken not available
        return Math.ceil(text.length / 4);
      }
      let encoding = this.encodingCache.get(tiktokenModel);
      if (!encoding) {
        encoding = tk.encoding_for_model(tiktokenModel);
        this.encodingCache.set(tiktokenModel, encoding);
      }
      const tokens = encoding.encode(text);
      return tokens.length;
    } catch (error) {
      logger.error('Error counting OpenAI tokens', { error });
      return Math.ceil(text.length / 4);
    }
  }

  static countTokensAnthropic(text: string): number {
    return Math.ceil(text.length / 3.5);
  }

  static countTokens(text: string, provider: string, model: string): number {
    if (provider.includes('openai')) {
      return this.countTokensOpenAI(text, model);
    } else if (provider.includes('anthropic')) {
      return this.countTokensAnthropic(text);
    }

    return Math.ceil(text.length / 4);
  }

  static estimateMessagesTokens(
    messages: Array<{ role: string; content: string }>,
    provider: string,
    model: string
  ): number {
    let totalTokens = 0;

    for (const message of messages) {
      totalTokens += this.countTokens(message.content, provider, model);

      if (provider.includes('openai')) {
        totalTokens += 4;
      } else {
        totalTokens += 3;
      }
    }

    if (provider.includes('openai')) {
      totalTokens += 3;
    }

    return totalTokens;
  }

  static estimateCompletionCost(
    inputTokens: number,
    outputTokens: number,
    model: string
  ): number {
    const pricing: Record<string, { input: number; output: number }> = {
      'gpt-3.5-turbo': { input: 0.0000005, output: 0.0000015 },
      'gpt-4': { input: 0.00003, output: 0.00006 },
      'gpt-4-turbo': { input: 0.00001, output: 0.00003 },
      'gpt-4o': { input: 0.000005, output: 0.000015 },
      'gpt-4o-mini': { input: 0.00000015, output: 0.0000006 },
      'claude-3-haiku': { input: 0.00000025, output: 0.00000125 },
      'claude-3-sonnet': { input: 0.000003, output: 0.000015 },
      'claude-3-opus': { input: 0.000015, output: 0.000075 },
      'claude-3-5-sonnet': { input: 0.000003, output: 0.000015 },
    };

    const modelPricing = pricing[model] || { input: 0.000001, output: 0.000002 };
    return inputTokens * modelPricing.input + outputTokens * modelPricing.output;
  }

  private static mapOpenAIModel(model: string): TiktokenModel {
    const modelMap: Record<string, TiktokenModel> = {
      'gpt-3.5-turbo': 'gpt-3.5-turbo',
      'gpt-4': 'gpt-4',
      'gpt-4-turbo': 'gpt-4-turbo',
      'gpt-4o': 'gpt-4o',
      'gpt-4o-mini': 'gpt-4o-mini',
    };

    return modelMap[model] || 'gpt-3.5-turbo';
  }

  static formatTokens(tokens: number): string {
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(1)}M`;
    } else if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    }
    return tokens.toString();
  }

  static estimatePromptTokens(prompt: string, provider: string, model: string): number {
    return this.countTokens(prompt, provider, model);
  }

  static cleanupEncodings(): void {
    this.encodingCache.forEach((encoding) => {
      try { encoding.free?.(); } catch {}
    });
    this.encodingCache.clear();
  }
}
